import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd'
import { SearchBar } from '@intelie/ui'
import i18n from 'live/services/i18n'
import { ColorStyle, CustomSettings, CustomSettingsByChannel, LiveMultiValuesMeta } from '../api'
import { AvailableChannelRow } from './AvailableChannelRow'
import { SelectedChannelRow } from './SelectedChannelRow'
import { RemovedChannelRow } from './RemovedChannelRow'
import { ChannelRowData } from './channelRowHelpers'

type ChannelSelectionChange = {
    selectedChannels: CustomSettingsByChannel[]
    excludedChannels: string[]
}

type Props = {
    meta: LiveMultiValuesMeta
    selectedChannels: CustomSettingsByChannel[]
    excludedChannels: string[]
    colorStyle: ColorStyle
    colorTextEnabled: boolean
    onChange: (next: ChannelSelectionChange) => void
}

// derives the excluded-mnemonics bookkeeping list from a before/after selectedChannels diff: any
// mnemonic dropped out of selection joins it (a Remove/Clear-all), any mnemonic that ended up selected
// leaves it (an Add/drag-in) - this is what makes a user's decision sticky against the default resolver,
// without every handler below having to manage the two arrays in lockstep by hand
// exported (only) so these pure helpers are directly unit-testable - no behavior change
export const syncExcluded = (
    previousSelected: CustomSettingsByChannel[],
    previousExcluded: string[],
    nextSelected: CustomSettingsByChannel[]
): string[] => {
    const nextSelectedMnemonics = new Set(nextSelected.map(channel => channel.mnemonic))
    const removedMnemonics = previousSelected
        .map(channel => channel.mnemonic)
        .filter(mnemonic => !nextSelectedMnemonics.has(mnemonic))

    return [...previousExcluded, ...removedMnemonics].filter(mnemonic => !nextSelectedMnemonics.has(mnemonic))
}

type SortDirection = 'asc' | 'desc' | null

const DROPPABLE_AVAILABLE = 'available-channels-droppable'
const DROPPABLE_SELECTED = 'selected-channels-droppable'

export const buildIncludedRows = (meta: LiveMultiValuesMeta, selectedChannels: CustomSettingsByChannel[]): ChannelRowData[] =>
    selectedChannels
        .map(({ mnemonic, settings }) => {
            const metadata = meta.channels?.find(channel => channel.mnemonic === mnemonic)

            return metadata ? { metadata, settings } : null
        })
        .filter(row => row != null)

// "Available" excludes dynamic channels a user has explicitly removed - those live in the Removed
// dynamic channels panel instead, so a channel never appears in two places at once
export const buildExcludedRows = (
    meta: LiveMultiValuesMeta,
    selectedChannels: CustomSettingsByChannel[],
    excludedChannels: string[]
): ChannelRowData[] => {
    const selectedMnemonics = new Set(selectedChannels.map(channel => channel.mnemonic))
    const excludedMnemonics = new Set(excludedChannels)

    return (meta.channels ?? [])
        .filter(channel => !selectedMnemonics.has(channel.mnemonic))
        .filter(channel => !(channel.selected === true && excludedMnemonics.has(channel.mnemonic)))
        .map(metadata => ({ metadata, settings: { customTitle: null, customColor: null } }))
}

// dynamic (query `selected: true`) channels a user has explicitly removed - each can be reset
// individually here, instead of only via the all-or-nothing Restore Defaults / Clear Exclusion List
export const buildRemovedDynamicRows = (meta: LiveMultiValuesMeta, excludedChannels: string[]): ChannelRowData[] => {
    const excludedMnemonics = new Set(excludedChannels)

    return (meta.channels ?? [])
        .filter(channel => channel.selected === true && excludedMnemonics.has(channel.mnemonic))
        .map(metadata => ({ metadata, settings: { customTitle: null, customColor: null } }))
}

export const matchesSearch = (row: ChannelRowData, searchExpression: string): boolean => {
    const lowerCaseSearch = searchExpression.toLowerCase()
    const title = (row.settings?.customTitle ?? row.metadata.title ?? '').toLowerCase()

    if (title.includes(lowerCaseSearch)) return true
    if (row.metadata.mnemonic.toLowerCase().includes(lowerCaseSearch)) return true

    return (row.metadata.category ?? '').toLowerCase().includes(lowerCaseSearch)
}

export type SortField = 'name' | 'category'
export type SortState = { field: SortField; direction: SortDirection } | null

// primarily by category, then by name within it - a channel with no category sorts as '' (before
// any named category, ascending). Both keys flip together with direction, same as name-only sort.
export const sortRows = (rows: ChannelRowData[], sort: SortState): ChannelRowData[] => {
    if (sort == null) return rows

    const { field, direction } = sort

    return [...rows].sort((a, b) => {
        const titleA = a.settings?.customTitle ?? a.metadata.title
        const titleB = b.settings?.customTitle ?? b.metadata.title
        const nameComparison = titleA.localeCompare(titleB)
        const comparison =
            field === 'category' ? (a.metadata.category ?? '').localeCompare(b.metadata.category ?? '') || nameComparison : nameComparison

        return direction === 'asc' ? comparison : -comparison
    })
}

// switching to a different sort field always starts fresh at 'asc' - only re-clicking the field
// already active cycles its direction through asc -> desc -> unsorted
export const cycleSortField = (current: SortState, field: SortField): SortState => {
    if (current?.field !== field) return { field, direction: 'asc' }
    if (current.direction === 'asc') return { field, direction: 'desc' }

    return null
}

// the directional arrow only shows on whichever field is actually the active sort key - the other
// field's button stays on the neutral fa-sort glyph, same as before this field was unsorted
export const sortIcon = (sort: SortState, field: SortField): string => {
    if (sort?.field !== field) return 'fa-sort'

    return sort.direction === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'
}

export const ChannelsTab = React.memo(function ChannelsTab({
    meta,
    selectedChannels,
    excludedChannels,
    colorStyle,
    colorTextEnabled,
    onChange
}: Props): JSX.Element {
    const [availSearch, setAvailSearch] = useState('')
    const [availSort, setAvailSort] = useState<SortState>(null)
    const [selSearch, setSelSearch] = useState('')
    const [selSort, setSelSort] = useState<SortState>(null)
    const [expandedMnemonic, setExpandedMnemonic] = useState<string>(null)

    const includedRows = useMemo(() => buildIncludedRows(meta, selectedChannels), [meta, selectedChannels])
    const excludedRows = useMemo(() => buildExcludedRows(meta, selectedChannels, excludedChannels), [
        meta,
        selectedChannels,
        excludedChannels
    ])
    const removedDynamicRows = useMemo(() => buildRemovedDynamicRows(meta, excludedChannels), [meta, excludedChannels])

    // commits a new Selected list, deriving the updated excluded-mnemonics bookkeeping from the diff -
    // every handler below only needs to compute the next selectedChannels, exactly as before this feature
    const commit = (nextSelected: CustomSettingsByChannel[]): void => {
        onChange({
            selectedChannels: nextSelected,
            excludedChannels: syncExcluded(selectedChannels, excludedChannels, nextSelected)
        })
    }

    const availableDisplayed = useMemo(
        () => sortRows(excludedRows.filter(row => matchesSearch(row, availSearch)), availSort),
        [excludedRows, availSearch, availSort]
    )
    const selectedDisplayed = useMemo(
        () => sortRows(includedRows.filter(row => matchesSearch(row, selSearch)), selSort),
        [includedRows, selSearch, selSort]
    )

    const updateChannelSettings = (mnemonic: string, patch: Partial<CustomSettings>): void => {
        commit(
            selectedChannels.map(channel =>
                channel.mnemonic === mnemonic ? { ...channel, settings: { ...channel.settings, ...patch } } : channel
            )
        )
    }

    const handleAdd = (mnemonic: string): void => {
        commit([...selectedChannels, { mnemonic, settings: { customTitle: null, customColor: null } }])
    }

    const handleRemove = (mnemonic: string): void => {
        commit(selectedChannels.filter(channel => channel.mnemonic !== mnemonic))
        if (expandedMnemonic === mnemonic) setExpandedMnemonic(null)
    }

    const handleToggleExpanded = (mnemonic: string): void => {
        setExpandedMnemonic(current => (current === mnemonic ? null : mnemonic))
    }

    const handleAddAll = (): void => {
        const additions = availableDisplayed.map(row => ({
            mnemonic: row.metadata.mnemonic,
            settings: { customTitle: null, customColor: null }
        }))

        commit([...selectedChannels, ...additions])
    }

    const handleClearAllSelected = (): void => {
        commit([])
        setExpandedMnemonic(null)
    }

    // commits a new Selected display order, keeping any selected channels currently hidden by search
    // at their existing relative position, appended after the visible/reordered ones
    const commitSelectedOrder = (reordered: ChannelRowData[]): void => {
        const visibleMnemonics = new Set(reordered.map(row => row.metadata.mnemonic))
        const hiddenSelected = selectedChannels.filter(channel => !visibleMnemonics.has(channel.mnemonic))

        setSelSort(null) // manual reordering only makes sense against the natural order
        commit([
            ...reordered.map(row => ({ mnemonic: row.metadata.mnemonic, settings: row.settings })),
            ...hiddenSelected
        ])
    }

    const onDragEnd = (result: DropResult): void => {
        if (!result.destination) return

        const { source, destination } = result

        if (source.droppableId === destination.droppableId) {
            if (source.droppableId !== DROPPABLE_SELECTED) return // reordering within Available has no meaning

            const reordered = Array.from(selectedDisplayed)
            const [movedRow] = reordered.splice(source.index, 1)
            reordered.splice(destination.index, 0, movedRow)

            commitSelectedOrder(reordered)

            return
        }

        if (destination.droppableId === DROPPABLE_SELECTED) {
            // dragged from Available into Selected, at the dropped position
            const movedRow = availableDisplayed[source.index]
            const newSelectedRow: ChannelRowData = {
                metadata: movedRow.metadata,
                settings: { customTitle: null, customColor: null }
            }

            const reordered = Array.from(selectedDisplayed)
            reordered.splice(destination.index, 0, newSelectedRow)

            commitSelectedOrder(reordered)
        } else {
            // dragged from Selected back into Available = remove it (matches the Remove button)
            const movedRow = selectedDisplayed[source.index]

            handleRemove(movedRow.metadata.mnemonic)
        }
    }

    return (
        <TabBody>
            <MainPanels>
                <DragDropContext onDragEnd={onDragEnd}>
                    <Panel>
                        <PanelHead>
                            <PanelTitle>{i18n('Available channels')}</PanelTitle>
                            <PanelCount>{excludedRows.length}</PanelCount>
                        </PanelHead>
                        <SearchBar
                            className="search-bar-custom-style"
                            filteredValue=""
                            handleFilter={setAvailSearch}
                            placeholder={i18n('Search')}
                        />
                        <PanelToolbar>
                            <ToolbarButton tone="accent" onClick={handleAddAll}>
                                <i className="fa fa-plus" />
                                {i18n('Add all')}
                            </ToolbarButton>
                            <SortGroup>
                                <ToolbarButton tone="accent" onClick={() => setAvailSort(current => cycleSortField(current, 'name'))}>
                                    <i className={`fa ${sortIcon(availSort, 'name')}`} />
                                    {i18n('Name')}
                                </ToolbarButton>
                                <ToolbarButton
                                    tone="accent"
                                    onClick={() => setAvailSort(current => cycleSortField(current, 'category'))}
                                >
                                    <i className={`fa ${sortIcon(availSort, 'category')}`} />
                                    {i18n('Category')}
                                </ToolbarButton>
                            </SortGroup>
                        </PanelToolbar>
                        {/* reserved (not unmounted) so both panels' headers are the same height and their
                            row lists start at the same vertical position - see the Selected panel's own */}
                        <Disclaimer style={{ visibility: 'hidden' }}>
                            {i18n('Manually reordering the channels overrides sorting.')}
                        </Disclaimer>
                        <Droppable droppableId={DROPPABLE_AVAILABLE}>
                            {provided => (
                                <RowsContainer ref={provided.innerRef} {...provided.droppableProps}>
                                    {availableDisplayed.map((row, index) => (
                                        <AvailableChannelRow
                                            key={row.metadata.mnemonic}
                                            row={row}
                                            index={index}
                                            colorStyle={colorStyle}
                                            colorTextEnabled={colorTextEnabled}
                                            onAdd={handleAdd}
                                        />
                                    ))}
                                    {provided.placeholder}
                                </RowsContainer>
                            )}
                        </Droppable>
                    </Panel>
                    <Panel>
                        <PanelHead>
                            <PanelTitle>{i18n('Selected channels')}</PanelTitle>
                            <PanelCount>{includedRows.length}</PanelCount>
                        </PanelHead>
                        <SearchBar
                            className="search-bar-custom-style"
                            filteredValue=""
                            handleFilter={setSelSearch}
                            placeholder={i18n('Search')}
                        />
                        <PanelToolbar>
                            <ToolbarButton tone="danger" onClick={handleClearAllSelected}>
                                <i className="fa fa-times" />
                                {i18n('Clear all')}
                            </ToolbarButton>
                            <SortGroup>
                                <ToolbarButton tone="accent" onClick={() => setSelSort(current => cycleSortField(current, 'name'))}>
                                    <i className={`fa ${sortIcon(selSort, 'name')}`} />
                                    {i18n('Name')}
                                </ToolbarButton>
                                <ToolbarButton
                                    tone="accent"
                                    onClick={() => setSelSort(current => cycleSortField(current, 'category'))}
                                >
                                    <i className={`fa ${sortIcon(selSort, 'category')}`} />
                                    {i18n('Category')}
                                </ToolbarButton>
                            </SortGroup>
                        </PanelToolbar>
                        <Disclaimer>{i18n('Manually reordering the channels overrides sorting.')}</Disclaimer>
                        <Droppable droppableId={DROPPABLE_SELECTED}>
                            {provided => (
                                <RowsContainer ref={provided.innerRef} {...provided.droppableProps}>
                                    {selectedDisplayed.map((row, index) => (
                                        <SelectedChannelRow
                                            key={row.metadata.mnemonic}
                                            row={row}
                                            index={index}
                                            expanded={expandedMnemonic === row.metadata.mnemonic}
                                            colorStyle={colorStyle}
                                            colorTextEnabled={colorTextEnabled}
                                            onRemove={handleRemove}
                                            onToggleExpanded={handleToggleExpanded}
                                            onUpdateSettings={updateChannelSettings}
                                        />
                                    ))}
                                    {provided.placeholder}
                                </RowsContainer>
                            )}
                        </Droppable>
                    </Panel>
                </DragDropContext>
            </MainPanels>
            <RemovedPanel>
                <PanelHead>
                    <PanelTitle>{i18n('Removed dynamic channels')}</PanelTitle>
                    <PanelCount>{removedDynamicRows.length}</PanelCount>
                </PanelHead>
                {removedDynamicRows.length > 0 ? (
                    <RemovedRowsContainer>
                        {removedDynamicRows.map(row => (
                            <RemovedChannelRow
                                key={row.metadata.mnemonic}
                                row={row}
                                colorStyle={colorStyle}
                                colorTextEnabled={colorTextEnabled}
                                onReset={handleAdd}
                            />
                        ))}
                    </RemovedRowsContainer>
                ) : (
                    <EmptyState>
                        {i18n('No dynamic channels have been removed.')}
                    </EmptyState>
                )}
            </RemovedPanel>
        </TabBody>
    )
})

const TabBody = styled.div`
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    background-color: var(--gray-200);
`

const MainPanels = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    gap: 16px;
`

// short and collapsible by nature (empty most of the time) - capped so it never competes with
// Available/Selected for space, unlike those it grows only as far as its own content needs
const RemovedPanel = styled.div`
    flex-shrink: 0;
    max-height: 160px;
    display: flex;
    flex-direction: column;
    background-color: var(--gray-0);
    border: 0.5px solid var(--gray-300);
    border-radius: 4px;
    overflow: hidden;
`

const RemovedRowsContainer = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    border-top: 0.5px solid var(--gray-300);

    &::-webkit-scrollbar-track {
        background-color: var(--gray-0);
    }

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background-color: var(--gray-500);
    }
`

const EmptyState = styled.div`
    padding: 0 16px 14px;
    font-size: 12px;
    font-style: italic;
    color: var(--gray-600);
`

const Panel = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    background-color: var(--gray-0);
    border: 0.5px solid var(--gray-300);
    border-radius: 4px;
    overflow: hidden;

    .search-bar-custom-style {
        margin: 0 16px 10px;
        height: 40px;
        padding: 3px 4px 0 4px;
        border-radius: 4px;
        border: 0.5px solid var(--gray-300);
        background-color: var(--gray-200);

        i {
            font-size: 14px;
            color: var(--blue-500); // same accent color as the other interactive icons
        }

        input {
            font-size: 14px;
            color: var(--gray-1000);
            background-color: transparent;
        }

        input::placeholder {
            color: var(--gray-600);
        }
    }
`

const PanelHead = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: baseline;
    gap: 8px;
    padding: 14px 16px 10px;
`

const PanelTitle = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: var(--gray-1000);
`

const PanelCount = styled.span`
    font-size: 12px;
    color: var(--gray-600);
`

const PanelToolbar = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px 10px;
`

// groups the two sort buttons (Name/Category) together on the toolbar's right side, as one visual
// unit distinct from Add all/Clear all on the left - space-between above only spaces these two groups
const SortGroup = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 2px;
`

const ToolbarButton = styled.button<{ tone?: 'accent' | 'danger' }>`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    line-height: 18px;
    color: ${({ tone }) => (tone === 'danger' ? 'var(--red-500)' : tone === 'accent' ? 'var(--blue-500)' : 'var(--gray-800)')};
    background: none;
    border: none;
    padding: 4px 6px;
    border-radius: 4px;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        background-color: var(--gray-200);
    }

    > i {
        font-size: 12px;
    }
`

const Disclaimer = styled.p`
    flex-shrink: 0;
    margin: -4px 16px 8px;
    font-size: 10px;
    line-height: 14px;
    font-style: italic;
    color: var(--gray-600);
`

const RowsContainer = styled.div`
    flex: 1;
    min-height: 0;
    border-top: 0.5px solid var(--gray-300);
    overflow-y: auto;

    &::-webkit-scrollbar-track {
        background-color: var(--gray-0);
    }

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background-color: var(--gray-500);
    }
`
